/**
 * `useIntakeIndexStore` — the rows the index table renders from (DI-2-1 §2.6).
 *
 * Ephemeral UI state (Zustand, per the repo's state rule): the files of the
 * current drop, what the worker has parsed and hashed so far, the phase, and
 * the counts the ONE status bar shows. Nothing here is sent anywhere; DI-2-4
 * adds the acquisition id and the payload-hash set.
 *
 * Results arrive one message per file; the worker client batches them and
 * applies each batch with ONE `applyBatch` call, so a 1 000-file drop costs
 * a handful of renders, not a thousand.
 */

import { createStore, useStore, type StoreApi } from "zustand";
import type { HashResult, ParsedImage } from "./parse/types.js";

export type IndexPhase =
  | "idle"
  | "indexing"
  | "hashing"
  | "done"
  | "cancelled"
  | "error";

export interface IndexRow {
  index: number;
  name: string;
  relativePath: string;
  size: number;
  parsed?: ParsedImage;
  hashes?: HashResult;
  error?: string;
}

export interface LoadedFile {
  name: string;
  relativePath: string;
  size: number;
}

/**
 * DI-3-S75 — a folder's `*_pos.txt` (`dropToFiles.ts` `aux`): never a row,
 * held with its bytes so the upload of a set from its folder sends it along.
 */
export interface AuxFile {
  name: string;
  /** As the images' `relativePath` (the dropped folder's name first, when there is one). */
  relativePath: string;
  /**
   * The wire's `relativeFolder` (inspection-3/v1 2.7.0): the file's folder
   * INSIDE the dropped folder, `''` for the dropped folder itself.
   */
  relativeFolder: string;
  size: number;
  file: Blob;
}

/**
 * DI-3-S75 — a collect result's `aux` as the index holds it; `folderName` =
 * the result's dropped folder (stripped from the wire's `relativeFolder`).
 */
export function auxOf(result: {
  aux?: readonly { file: File; relativePath: string }[];
  folderName: string | null;
}): AuxFile[] {
  const root = result.folderName;
  return (result.aux ?? []).map((a) => {
    const i = a.relativePath.lastIndexOf("/");
    const folder = i < 0 ? "" : a.relativePath.slice(0, i);
    const relativeFolder =
      root === null
        ? folder
        : folder === root
          ? ""
          : folder.startsWith(`${root}/`)
            ? folder.slice(root.length + 1)
            : folder;
    return {
      name: a.file.name,
      relativePath: a.relativePath,
      relativeFolder,
      size: a.file.size,
      file: a.file,
    };
  });
}

export interface IndexState {
  sessionId: string | null;
  /** The folder the files came from, when known (picker / webkitdirectory root). */
  folderName: string | null;
  phase: IndexPhase;
  rows: IndexRow[];
  total: number;
  parsedCount: number;
  hashedCount: number;
  /** Non-image entries seen in the drop and left out (counted, not silent). */
  skipped: number;
  /** DI-3-S75 — the drop's `*_pos.txt` files (no rows, no counts, no UI). */
  aux: AuxFile[];
  errors: string[];

  load: (args: {
    sessionId: string;
    folderName: string | null;
    files: readonly LoadedFile[];
    skipped: number;
    aux?: readonly AuxFile[];
  }) => void;
  /**
   * DI-3-C — "Add another folder": rows appended after the ones already
   * there (indexes continue), the phase back to `indexing`. The runner feeds
   * them with an index client of their own (offset by the rows before).
   */
  append: (args: {
    files: readonly LoadedFile[];
    skipped: number;
    aux?: readonly AuxFile[];
  }) => void;
  applyBatch: (batch: {
    parsed?: readonly { index: number; result: ParsedImage }[];
    hashed?: readonly { index: number; hashes: HashResult }[];
    errors?: readonly { index?: number; message: string }[];
  }) => void;
  setPhase: (phase: IndexPhase) => void;
  reset: () => void;
}

const EMPTY = {
  sessionId: null,
  folderName: null,
  phase: "idle" as IndexPhase,
  rows: [] as IndexRow[],
  total: 0,
  parsedCount: 0,
  hashedCount: 0,
  skipped: 0,
  aux: [] as AuxFile[],
  errors: [] as string[],
};

export function createIndexStore(): StoreApi<IndexState> {
  return createStore<IndexState>()((set) => ({
    ...EMPTY,
    load: ({ sessionId, folderName, files, skipped, aux }) => {
      set({
        ...EMPTY,
        sessionId,
        folderName,
        skipped,
        aux: aux ? aux.slice() : [],
        total: files.length,
        phase: files.length > 0 ? "indexing" : "idle",
        rows: files.map((f, index) => ({
          index,
          name: f.name,
          relativePath: f.relativePath,
          size: f.size,
        })),
      });
    },
    append: ({ files, skipped, aux }) => {
      set((s) => ({
        aux: aux && aux.length > 0 ? [...s.aux, ...aux] : s.aux,
        rows: [
          ...s.rows,
          ...files.map((f, i) => ({
            index: s.rows.length + i,
            name: f.name,
            relativePath: f.relativePath,
            size: f.size,
          })),
        ],
        total: s.total + files.length,
        skipped: s.skipped + skipped,
        phase: files.length > 0 ? "indexing" : s.phase,
      }));
    },
    applyBatch: ({ parsed = [], hashed = [], errors = [] }) => {
      set((s) => {
        if (parsed.length === 0 && hashed.length === 0 && errors.length === 0)
          return s;
        const rows = s.rows.slice();
        let parsedCount = s.parsedCount;
        let hashedCount = s.hashedCount;
        for (const p of parsed) {
          const row = rows[p.index];
          if (!row) continue;
          if (!row.parsed) parsedCount++;
          rows[p.index] = { ...row, parsed: p.result };
        }
        for (const h of hashed) {
          const row = rows[h.index];
          if (!row) continue;
          if (!row.hashes) hashedCount++;
          rows[h.index] = { ...row, hashes: h.hashes };
        }
        const errs = s.errors.slice();
        for (const e of errors) {
          errs.push(e.message);
          if (e.index === undefined) continue;
          const row = rows[e.index];
          if (row) rows[e.index] = { ...row, error: e.message };
        }
        let phase = s.phase;
        if (phase === "indexing" && parsedCount >= s.total) phase = "hashing";
        return { rows, parsedCount, hashedCount, errors: errs, phase };
      });
    },
    setPhase: (phase) => {
      set({ phase });
    },
    reset: () => {
      set({ ...EMPTY });
    },
  }));
}

/** The page's store. Tests reset it (`intakeIndexStore.getState().reset()`) between cases. */
export const intakeIndexStore = createIndexStore();

export function useIntakeIndexStore<T>(selector: (s: IndexState) => T): T {
  return useStore(intakeIndexStore, selector);
}
